import React, { useMemo } from 'react';
import { Box, Center, Heading, Text } from '@chakra-ui/react';
import { Line } from 'react-chartjs-2';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, TimeScale, Tooltip, Legend);

const palette = [
  '#3182CE',
  '#38A169',
  '#D69E2E',
  '#805AD5',
  '#DD6B20',
  '#319795',
  '#E53E3E',
  '#718096'
];

const isFoundationalSkill = (skill) => skill?.type?.toLowerCase?.() === 'foundational';

const SkillHistoryChart = ({ skillHistory }) => {
  const chartData = useMemo(() => {
    const grouped = new Map();
    const history = Array.isArray(skillHistory) ? skillHistory : [];

    history
      .filter((skill) => isFoundationalSkill(skill) && skill.created && skill.level != null)
      .forEach((skill) => {
        const label = skill.skillname || `Skill ${skill.skillid}`;
        const points = grouped.get(label) ?? [];
        points.push({
          x: skill.created,
          y: Number(skill.level)
        });
        grouped.set(label, points);
      });

    const datasets = Array.from(grouped, ([label, points], index) => {
      const color = palette[index % palette.length];
      return {
        label,
        data: points.sort((a, b) => new Date(a.x) - new Date(b.x)),
        borderColor: color,
        backgroundColor: color,
        tension: 0.25,
        pointRadius: 3,
        fill: false
      };
    });

    return datasets.length > 0 ? { datasets } : null;
  }, [skillHistory]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: 'nearest' },
      scales: {
        x: {
          type: 'time',
          time: { tooltipFormat: 'PP' },
          ticks: { maxRotation: 0 }
        },
        y: {
          min: 0,
          max: 5,
          ticks: { stepSize: 1, precision: 0 }
        }
      },
      plugins: {
        legend: { position: 'bottom' }
      }
    }),
    []
  );

  return (
    <Box bg="white" boxShadow="md" borderRadius="md" p={6} mb={6}>
      <Heading as="h2" size="md" mb={4}>
        Skill history
      </Heading>
      {chartData ? (
        <Box minH="260px">
          <Line data={chartData} options={chartOptions} />
        </Box>
      ) : (
        <Center minH="160px">
          <Text color="gray.500">No foundational skill history available.</Text>
        </Center>
      )}
    </Box>
  );
};

export default SkillHistoryChart;
